import checkoutData from "../Interface/copyCheckoutData";
import {
  FibreList,
  ExtendedLinkedList,
  LinkedChildList,
} from "../Constants/GlobalConstant";

import { getLabelAndValue } from "./SchemaValidation";
import { saveUserSessionData } from "./CheckSession";
import { getLabelTypeForSession } from "./CheckSession";
import { sessionCache } from "./sessionCache";
import {
  GetSchemaPageIdFromLabelType,
  GetFirstPageIdFromLabelType,
  GetSchemaPageItems,
  GetSystemParameterValueByName,
  GetFactoryCodes,
} from "./SchemaAPICalls";
import {
  columnsToHideForZaraPriceTicket,
  columnsToHideForZaraStickers,
} from "../Interface/ZaraColumn";
import {
  requestOptions,
  VIPSDataServiceURL,
  MenuURL,
} from "../Constants/GlobalConstant";
import store from "../Store/store";
import SessionArray from "../Store/SessionArray";
import userSlice from "../Store/userSlice";
import userFieldsSlice from "../Store/userFieldsSlice";
import { addLabelItem, clearLabels } from "../Store/LabelTypeLogic";

// ---------------------------------------------------------------
// Utility Functions
import { applyRoundIfCondition, clampMinIfCondition } from "./ConditionalValidation";

// Returns session object ID using label type from labelSessionArray.
// Falls back to label name or PageID when label type is not available.
export function getSessionObjectId(searchParams, labelSessionArray) {
  const label = searchParams.get("label");
  // If labelSessionArray not passed, try to derive from localStorage
  let labelArr = labelSessionArray;
  if (!Array.isArray(labelArr) || labelArr.length === 0) {
    try {
      const sa = JSON.parse(localStorage.getItem("sessionArray")) || [];
      const labelListItem = sa.find(item => item.Key === "LabelList");
      if (labelListItem) {
        const jsonData = JSON.parse(labelListItem.Value);
        const occLabelList = jsonData.OCCLabelList || [];
        labelArr = [...new Set(occLabelList.map(item => item.LabelReference))];
      }
    } catch (_) {}
  }
  return getLabelTypeForSession(label, labelArr) || searchParams.get("PageID");
}

export function mapCostDetails(details, mapping) {
  return details?.map((item) => {
    const mappedItem = {};
    for (const key in item) {
      if (mapping[key]) {
        if (Array.isArray(mapping[key])) {
          mapping[key].forEach((field) => {
            mappedItem[field] = item[key];
          });
        } else {
          mappedItem[mapping[key]] = item[key];
        }
      }
    }
    return mappedItem;
  });
}

export function splitValue(value, rowIndex, header, mappedValues) {
  let start = 0;
  let chunkIndex = 1;
  while (start < value.length) {
    const end = Math.min(start + 250, value.length);
    const segment = value.substring(start, end);
    const newHeader = chunkIndex > 1 ? `${header}-${chunkIndex}` : header;
    if (!mappedValues[newHeader]) {
      mappedValues[newHeader] = [];
    }
    mappedValues[newHeader].push({ value: segment, rowIndex });
    start = end;
    chunkIndex++;
  }
}

// ---------------------------------------------------------------
// Label Helper Functions

export const addLabelFromHelper = (labelData) => {
  store.dispatch(addLabelItem(labelData));
};

export const removeLabelFromHelper = () => {
  store.dispatch(clearLabels());
};

// ---------------------------------------------------------------
// Data Extraction and Processing

export function copyCheckoutData(data, user_id, searchParams) {
  const order = data.Order[0];
  const deliveryAddress = data.DeliveryAddress;

  Object.assign(checkoutData, {
    customerId: order.customer_id,
    shippingInstruction: order.shipping_instructions,
    PrinterNotes: order.printer_notes,
    VendorNotes: order.vendor_notes,
    SpecialInstruction: order.spec_instr,
    orderLineInstructions: order.order_line_instructions,
    shippingMark: order.ship_mark,
    samplingAndPackagingCode: order.spCode,
    selectedCarriers: order.carrier_code,
    selectedFreightTerms: order.freight_term,
    retailerPurchaseOrderNo: order.rbo_pono,
    custPoNumber: order.customer_PO,
  });

  Object.assign(checkoutData, {
    DelAddress1: deliveryAddress.delivery_address1,
    DelAddress2: deliveryAddress.delivery_address2,
    DelAddress3: deliveryAddress.delivery_address3,
    DelAddress4: deliveryAddress.delivery_address4,
    DelPostalCode: deliveryAddress.delivery_post_code,
    DelCustomerName: deliveryAddress.delivery_customer_name,
    DelContactName: deliveryAddress.contact_name,
    DelCountry: deliveryAddress.delivery_country_code?.trim(),
    DeLAddressDrop: deliveryAddress.contact_name,
    DefaultDelivery: deliveryAddress.contact_name,
    shipToID: deliveryAddress.shipToID,
    DeliveryAddress: deliveryAddress,
    requestNewAddress: deliveryAddress.requestNewAddress,
  });
   let firstPageID = null;
  try {
    const raw = localStorage.getItem("firstPageID");
    firstPageID = raw ? JSON.parse(raw) : null;
  } catch (_) {
    firstPageID = null;
  }
  const labelOrPageID = searchParams.get("OCC")
    ? searchParams.get("PageID")
    : (firstPageID ?? searchParams.get("PageID"));
 // const labelOrPageID = searchParams.get("label") || searchParams.get("PageID");
    sessionCache.set(`${user_id}${labelOrPageID}checkoutForm`, checkoutData);
  const invoiceAddressData = { invoiceAddress: data.InvoiceAddress };
  sessionCache.set(`${user_id}${labelOrPageID}autocomplete`, invoiceAddressData);
}
export function getValueFromCSV(i, headerIndex, validation, csvData) {
  // If column not found in CSV headers, return null/empty based on validation
  if (headerIndex < 0) {
    return validation?.mandatory ? "" : null;
  }
  
  const value = csvData?.data[i]?.[csvData?.headers?.[headerIndex]];
  return value === undefined ? (validation?.mandatory ? "" : value) : value;
}

export function updateTableData(
  fieldName,
  headerIndex,
  value1,
  csvData,
  _tableData,
  i
) {
  _tableData[fieldName] = getValueFromCSV(
    i,
    headerIndex,
    value1.validation,
    csvData
  );
  if (value1.prefix === "QTY") {
    _tableData["INQ" + value1.tagName] = getValueFromCSV(
      i,
      headerIndex,
      value1.validation,
      csvData
    );
  }
}

export const extractCode = async (
  string,
  _schemaName,
  regexString,
  skipRFIDLogic
) => {
  if (typeof string !== "string" || string.trim() === "") return null;

  const regexPattern = new RegExp(regexString);
  const match = string.match(regexPattern);
  if (!match) return null;

  string.toLowerCase().includes("_non_rfid")
    ? addLabelFromHelper("non_rfid")
    : addLabelFromHelper("rfid");

  if (skipRFIDLogic) return match[1];

  let code = match[1];
  if (!string.toLowerCase().includes("_non_rfid")) {
    code = "RFID" + code;
  }
  return code;
};

export function getVariableDesc(data, variableText) {
  const foundVariable = data?.find((item) => item.variable_id === variableText);
  return foundVariable ? foundVariable.variable_text : null;
}

function getValueByKey(
  key,
  costDetails,
  value,
  orderType,
  _formData,
  formData,
  numberOfLinesToDisplay,
  fieldName
) {
  var validValue = "";
  if (costDetails[key] && costDetails[key] !== "") {
    if (
      value.prefix === "SLI" ||
      value.prefix === "ELL" ||
      value.prefix === "LLI" ||
      value.prefix === "ILI"
    ) {
      validValue = value.options.some(
        (option) => option.value === costDetails[key]
      )
        ? costDetails[key]
        : "";
    } else if (value.prefix === "ELC" && value.hasChildList) {
      let ELCValue = "";
      for (const [key2, value2] of Object.entries(value.lists)) {
        if (parseInt(key2) === parseInt(_formData["ELL" + "Size Chart"])) {
          const option = value2.find((x) => x.label === costDetails[key]);
          ELCValue = option?.value;
          break;
        }
      }
      validValue = ELCValue ? costDetails[key] : "";
    } else if (value.prefix === "TXT") {
      const trimmedValue = value.validation?.pictureMask
        ? costDetails[key].substring(
            0,
            value.validation?.pictureMask?.replace(/"/g, "").length
          )
        : costDetails[key];
      validValue = trimmedValue;
    } else {
      validValue = costDetails[key];
    }
  } else {
    if (value.prefix === "BAR" && orderType === "UK") {
      validValue = costDetails["UK EAN 13"];
    }
    if (numberOfLinesToDisplay <= 1) {
      if (value.prefix === "CHK") {
        validValue = formData[fieldName][0][key]
          ? formData[fieldName][0][key]
          : "N";
      } else {
        validValue = formData[fieldName][0][key];
      }
    }
  }

  return validValue;
}

export async function processCostDetails(
  uploadedFileTesco,
  value1,
  _formData,
  _tableData,
  tescoFiledMapping,
  i,
  details,
  _sizeChart,
  _fileType,
  orderType,
  ELCDropDown,
  setELCDropDown,
  schemaID,
  formData,
  numberOfLinesToDisplay,
  fieldName
) {
  if (uploadedFileTesco) {
    const mappedData = mapCostDetails(details, tescoFiledMapping);

    if (mappedData && mappedData[i]) {
      var returnValue = getValueByKey(
        value1.fieldName,
        mappedData[i],
        value1,
        orderType,
        _formData,
        formData,
        numberOfLinesToDisplay,
        fieldName
      );
      _tableData[value1.fieldName] = returnValue;

      if (value1.prefix == "QTY") {
        const base = parseInt(returnValue, 10);
        const rounded = applyRoundIfCondition(base, value1.validation.roundOffQty, value1.validation);
        const clamped = clampMinIfCondition(rounded, value1.validation.minValue, value1.validation);
        _tableData[value1.fieldName] = String(clamped);
        _tableData["INQ" + value1.tagName] = returnValue.toString();
      }
      if (value1.prefix === "ELC") {
        let ELCValue = "";
        if (value1.hasChildList) {
          for (const [key, value] of Object.entries(value1.lists)) {
            if (parseInt(key) === parseInt(_formData["ELL" + "Size Chart"])) {
              const option = value.find((x) => x.label === returnValue);
              ELCValue = option?.value;
              break;
            }
          }
        }

        let changeDropDownVal = { ...ELCDropDown };

        setELCDropDown(changeDropDownVal);
        if (value1.hasChildList) {
          if (ELCValue) {
            fetchParentELCHeaderData2(
              value1,
              i,
              ELCValue,
              changeDropDownVal,
              setELCDropDown,
              schemaID
            );
          }
        }

        _tableData["ELC" + value1.fieldName] = ELCValue;
      }

      if (orderType === "CE2") {
        if (value1.prefix == "QTY") {
          const returnValue = getValueByKey("TotUnitCE", mappedData[i], value1);
          const base = parseInt(returnValue, 10);
          const rounded = applyRoundIfCondition(base, value1.validation.roundOffQty, value1.validation);
          const clamped = clampMinIfCondition(rounded, value1.validation.minValue, value1.validation);
          _tableData[value1.fieldName] = String(clamped);
          _tableData["INQ" + value1.tagName] = getValueByKey(
            "TotUnitCE",
            mappedData[i],
            value1
          );
        }
        if (value1.prefix == "BAR") {
          _tableData[value1.fieldName] = getValueByKey(
            "CE EAN 13",
            mappedData[i],
            value1
          );
        }
        if (value1.fieldName == "UK Size") {
          _tableData[value1.fieldName] = getValueByKey(
            "UKSizeCE",
            mappedData[i],
            value1,
            orderType,
            _formData
          );
        }
      }
    } else {
      _tableData[value1.fieldName] = "";
    }
  }
}
export async function fetchParentELCHeaderData2(
  element,
  tdkeys,
  selectedValue,
  catalogueDropDown,
  setELCDropDown,
  schemaID
) {
  // const changeDropDownVal = { ...catalogueDropDown };
  const keyIndex = element?.schemaItem ?? 0; // added default
  // Added guard: ensure structure exists
  if (!catalogueDropDown[keyIndex]) {
    catalogueDropDown[keyIndex] = { key: {}, keyValues: {} };
  }
  if (!catalogueDropDown[keyIndex].key) {
    catalogueDropDown[keyIndex].key = {};
  }
  if (!catalogueDropDown[keyIndex].keyValues) {
    catalogueDropDown[keyIndex].keyValues = {};
  }
  catalogueDropDown[keyIndex]["key"][tdkeys] = selectedValue;
  const getSchemaChildLinkedListItems = `${VIPSDataServiceURL}/Schema/GetSchemaChildLinkedListItems?schemaID=${schemaID}&itemID=${selectedValue}`;

  // if (tdkeys === rowIndex && !checkAndSetItem(catalogueDropDown, rowIndex)) {
  // setLoadingStates((prevStates) => ({
  //   ...prevStates,
  //   [tdkeys]: true, // Set loading state for this row to true
  // }));
  try {
    const response = await fetch(getSchemaChildLinkedListItems, requestOptions);
    const data = await response.json();
    catalogueDropDown[keyIndex]["keyValues"][tdkeys] = data;

    setELCDropDown(catalogueDropDown);
    //setFieldValue(`${name}[${tdkeys}].${element.fieldName}`, selectedValue);
  } catch (error) {
    console.error("Error fetching key values:", error);
  } finally {
    setELCDropDown(catalogueDropDown);
    // setLoadingStates((prevStates) => ({
    //   ...prevStates,
    //   [tdkeys]: false, // Set loading state for this row to false
    // }));
  }
  //}
}
export async function processCostDetails2(
  uploadedFileTesco,
  value,
  _formData,
  _tableData,
  tescoFiledMapping,
  details,
  sizeChart,
  _fileType,
  _orderType
) {
  if (uploadedFileTesco) {
    const mappedData = mapCostDetails(details, tescoFiledMapping);
    if (mappedData && mappedData[0]) {
      if (value.fieldName === "Size Chart") {
        _formData[value.fieldName] = sizeChart?.trim();
        _formData["ELL" + value.fieldName] = value?.options.find(
          (x) => x.label === sizeChart?.trim()
        )?.value;
      } else {
        var returnValue = getValueByKey(value.fieldName, mappedData[0], value);

        _formData[value.fieldName] = returnValue;
      }
    }
  }
}

// ---------------------------------------------------------------
// Data Cleaning Functions

export function removeSeperatorValue(schema, tableName, values) {
  if (values) {
    if (
      schema.dataType === ExtendedLinkedList ||
      schema.dataType === LinkedChildList ||
      schema.dataType === "ELC"
    ) {
      if (tableName) {
        for (const data of values[tableName]) {
          if (
            data[schema.fieldName]?.length > 0 &&
            data[schema.fieldName]?.includes("|")
          )
            data[schema.fieldName] = getLabelAndValue(
              data[schema.fieldName]
            )[0];
        }
      } else {
        if (
          values[schema.fieldName]?.length > 0 &&
          values[schema.fieldName]?.includes("|")
        )
          values[schema.fieldName] = getLabelAndValue(
            values[schema.fieldName]
          )[0];
      }
    }
  }
}
export function RemoveCheckBoxValue(schema, tableName, values) {
  if (schema.prefix === "CHK") {
    for (const data of values[tableName]) {
      if (
        !Object.values(data).some(
          (x) => x?.length > 0 && x !== data[schema.fieldName]
        )
      ) {
        data[schema.fieldName] = "";
      }
    }
  }
}

// ---------------------------------------------------------------
// RFID Logic

export async function checkRFIDLogic(schemaName) {
  let skipRFIDLogic = false;
  if (typeof GetSystemParameterValueByName === "function") {
    try {
      const data = await GetSystemParameterValueByName(
        "REACT_SKIP_RFID_VALIDATION"
      );
      if (Array.isArray(data)) {
        skipRFIDLogic = data.some(
          (item) => item.Schema === schemaName && item.Status === "Y"
        );
      }
    } catch (e) {
      console.log("Exception in CheckRFIDLogic method - ", e);
    }
  }
  return skipRFIDLogic;
}

// ---------------------------------------------------------------
// Form Initialization

export const initForm = async (
  regexString,
  searchParams,
  formSchema,
  uniqueProductIds,
  copyOrder,
  copyOrderData,
  labelReference,
  user_id,
  setFormData,
  csvData,
  userSelection,
  schemaName,
  uploadedFileTesco,
  details,
  tescoFiledMapping,
  sizeChart,
  fileType,
  orderType,
  _pageName,
  ELCDropDown,
  setELCDropDown,
  schemaID,
  formData,
  ediReadonlyMode
) => {
  removeLabelFromHelper();
  let _formData = {};
  let _tableData = {};
  let count = 0;

  // For copy orders: if the selected label (labelReference) differs from the
  // original order's label but shares the same label type, use the original
  // label name so the filter matches the copy-order data.
  let effectiveLabelRef = labelReference;
  if (copyOrder && labelReference && uniqueProductIds?.length) {
    const originalNames = uniqueProductIds.map((pid) => pid.split("[")[0]);
    if (!originalNames.includes(labelReference)) {
      // labelReference doesn't match any product — fall back to original
      effectiveLabelRef = originalNames[0];
    }
  }


  checkRFIDLogic(schemaName).then(async (skipRFIDLogic) => {
    const csvFileLength = csvData?.data?.length;
    for (var value of Object.values(formSchema)) {

      // --- Added block: collect unique productIdFirstPart for CTB pages ---
      if (
        value.prefix === "CTB" &&
        copyOrder &&
        !uploadedFileTesco &&
        copyOrderData?.Order?.[0]?.VIPSOrderDetailItem
      ) {
        const uniqueLabelRef1Values = new Set();
        copyOrderData.Order[0].VIPSOrderDetailItem.forEach((item) => {
          const productIdParts = (item.original_product_id
            ? item.original_product_id
            : item.product_id
          )?.split("[");
          const productIdFirstPart = productIdParts?.[0]?.trim();
          if (productIdFirstPart) uniqueLabelRef1Values.add(productIdFirstPart);
        });
        const uniqueLabelRef1Array = Array.from(uniqueLabelRef1Values);
        if (uniqueLabelRef1Array.length > 0) {
          sessionCache.set(
            user_id + getSessionObjectId(searchParams) + "uniqueLabelRef1",
            uniqueLabelRef1Array
          );
        }
      }
      // --- End added block ---

      if (value.prefix === "LAB") {
        if (copyOrder && !uploadedFileTesco) {
          if (uniqueProductIds[count]) {
            _formData[value.fieldName] = uniqueProductIds[count];
          }
          count = count + 1;
        }
      } else if (value.prefix === "CHK2") {
        _formData[value.fieldName] = "N";
      } else if (value.dataType === "Table" || value.prefix === "CTB") {
        _tableData = {};
        _formData[value.fieldName] = [];
        let filteredData = [];

        if (copyOrder && !uploadedFileTesco) {
          const data = copyOrderData?.Order[0]?.VIPSOrderDetailItem;
          filteredData = data.filter((item) => {
            const productIdParts = item.original_product_id
              ? item.original_product_id.split("[")
              : item.product_id.split("[");
            const productIdFirstPart = productIdParts[0].trim();
            return (
              productIdFirstPart ===
                (!effectiveLabelRef
                  ? uniqueProductIds[0].split("[")[0]
                  : effectiveLabelRef) && item.VIPSVarOrderDetailItem.length > 0
            );
          });
        }
        const numberOfLinesToDisplay =value.prefix==="CTB"?filteredData.length:csvFileLength
          ? csvFileLength
          : ediReadonlyMode && details?.length
          ? details.length
          : copyOrder
          ? filteredData.length > 0 && value.numberOfLinesToDisplay > 1
            ? filteredData.length
            : value.numberOfLinesToDisplay
          : value.numberOfLinesToDisplay;
        for (var i = 0; i < numberOfLinesToDisplay; i++) {
          _tableData = {};
          for (var value1 of Object.values(value.schemaPageSubItems)) {
            if (value1.prefix === "CHK") {
              _tableData[value1.fieldName] = "N";
            } else {
              if (copyOrder && !uploadedFileTesco) {
                const data = copyOrderData.Order[0].VIPSOrderDetailItem;

                copyCheckoutData(copyOrderData, user_id, searchParams);
                const filteredData = data.filter((item) => {
                  const productIdParts = item.original_product_id
                    ? item.original_product_id.split("[")
                    : item.product_id.split("[");
                  const productIdFirstPart = productIdParts[0].trim();
                  return (
                    productIdFirstPart ===
                      (!effectiveLabelRef
                        ? uniqueProductIds[0].split("[")[0]
                        : effectiveLabelRef) &&
                    item.VIPSVarOrderDetailItem.length > 0
                  );
                });
                                const variableTextToSearch = value1.tagName + "_1";

                if (filteredData[i]) {
                  const variableDesc = getVariableDesc(
                    filteredData[i].VIPSVarOrderDetailItem,
                    variableTextToSearch
                  );

                  _tableData[value1.fieldName] = variableDesc;
                  if (value1.prefix === "QTY") {
                    _tableData["INQ" + value1.tagName] = getVariableDesc(
                      filteredData[i].VIPSVarOrderDetailItem,
                      "INQ" + variableTextToSearch
                    );
                  }
                } else {
                  _tableData[value1.fieldName] = "";
                }
              } else if (csvData) {
                //const orderNoIndex = csvData.headers.indexOf(value1.fieldName);
                let headerIndex = -1;
                for (let i = 0; i < csvData.headers.length; i++) {
                  if (csvData.headers[i] === value1.fieldName) {
                    headerIndex = i;
                    break;
                  }
                }
                if (value1.prefix == "LAB") {
                  _tableData[value1.fieldName] = await extractCode(
                    csvData?.data[i]?.[csvData?.headers?.[headerIndex]] ===
                      undefined
                      ? value1.validation.mandatory
                        ? ""
                        : csvData?.data[i]?.[csvData?.headers?.[headerIndex]]
                      : csvData?.data[i]?.[csvData?.headers?.[headerIndex]],
                    schemaName,
                    regexString,
                    skipRFIDLogic
                  );
                } else {
                  if (userSelection) {
                    const tabColumnsToHide =
                      value.tabValue === 1
                        ? columnsToHideForZaraPriceTicket
                        : columnsToHideForZaraStickers;
                    if (tabColumnsToHide.includes(value1.fieldName)) {
                      // Check if this row is a matching PO (has PO_NUMBER value)
                      const rowPO = csvData?.data[i]?.["PO_NUMBER"];
                      const isMatchingPO = rowPO !== null && rowPO !== undefined && rowPO !== "";
                      
                      if (isMatchingPO) {
                        _tableData[value1.fieldName] = "";  // Blank for matching POs
                      } else {
                        updateTableData(
                          value1.fieldName,
                          headerIndex,
                          value1,
                          csvData,
                          _tableData,
                          i
                        );  // Show values for non-matching POs
                      }
                    } else {
                      updateTableData(
                        value1.fieldName,
                        headerIndex,
                        value1,
                        csvData,
                        _tableData,
                        i
                      );
                    }
                  } else {
                    updateTableData(
                      value1.fieldName,
                      headerIndex,
                      value1,
                      csvData,
                      _tableData,
                      i
                    );
                  }
                }
              } else if (uploadedFileTesco) {
                processCostDetails(
                  uploadedFileTesco,
                  value1,
                  _formData,
                  _tableData,
                  tescoFiledMapping,
                  i,
                  details,
                  sizeChart,
                  fileType,
                  orderType,
                  ELCDropDown,
                  setELCDropDown,
                  schemaID,
                  formData,
                  value.numberOfLinesToDisplay,
                  value.fieldName
                );
              } else {
                _tableData[value1.fieldName] = "";
              }
            }
          }

          _formData[value.fieldName].push(_tableData);
        }
      } else if (value.prefix === "FIB") {
        let filteredData = [];
        if (copyOrder && !uploadedFileTesco) {
          const data = copyOrderData?.Order[0]?.VIPSOrderDetailItem;
          filteredData = data.filter((item) => {
            const productIdParts = item.original_product_id
              ? item.original_product_id.split("[")
              : item.product_id.split("[");
            const productIdFirstPart = productIdParts[0].trim();
            return (
              productIdFirstPart === effectiveLabelRef ||
              item.VIPSVarOrderDetailItem.length > 0
            );
          });
          const variableTextToSearch = value.tagName + "_1";

          const FibrefilteredData =
            filteredData[0].VIPSVarOrderDetailItem.filter(
              (item) => item.variable_desc === value.fieldName
            );
          _formData[value.fieldName] = [];

          function processGarment(variableData, headerList) {
            var matchingHeaderCount = true;
            variableData.forEach((item) => {
              const matchingHeader = headerList.find((header) =>
                item.variable_text===header.value
              );

              const percentageMatch = item.variable_text.match(/(\d+)%/);
              const percentage = percentageMatch
                ? parseInt(percentageMatch[1])
                : 0;
              if (matchingHeader && matchingHeaderCount) {
                matchingHeaderCount = false;
                const garmentData = {
                  garment: [],
                  percentage: 100,
                  isSave: true,
                  isEdit: true,
                  fibres: [],
                };
                garmentData.garment.push({
                  garment: matchingHeader.value,
                  //percentage: percentage,
                });
                _formData[value.fieldName].push(garmentData);
              } else {
                matchingHeaderCount = true;
                const lastGarmentKey = Object.keys(
                  _formData[value.fieldName]
                ).pop();
                if (!matchingHeader && matchingHeaderCount && !lastGarmentKey) {
                  matchingHeaderCount = false;
                  const garmentData = {
                    garment: [],
                    percentage: 100,
                    isSave: true,
                    isEdit: true,
                    fibres: [],
                  };
                  garmentData.garment.push({
                    garment: "",
                  });
                  _formData[value.fieldName].push(garmentData);
                } else {
                  if (lastGarmentKey) {
                    const lastGarment =
                      _formData[value.fieldName][lastGarmentKey];
                    // const percentageMatch = item.variable_text.match(/(\d+)%/);
                    //const percentageMatch = item.variable_text.match(/^(\d+)%/);
                    const percentageMatch =
                      item.variable_text.match(/^(\d+(?:\.\d+)?)%/);
                    const percentage = percentageMatch
                      ? parseFloat(percentageMatch[1])
                      : 0;
                    const fibreText = percentageMatch
                      ? item.variable_text.replace(/\d+(?:\.\d+)?%/, "").trim()
                      : item.variable_text;
                    if (matchingHeader) {
                      lastGarment.garment.push({
                        garment: fibreText,
                        //percentage: percentage,
                      });
                    } else {
                      lastGarment.fibres.push({
                        fibre: fibreText,
                        percentage: percentage,
                      });
                    }
                  } else {
                    const generalGarment = value.fieldName;

                    // Add the fibre to the default "Fibres" garment
                    _formData[generalGarment]?.fibres.push({
                      fibre: item.variable_text,
                      percentage: percentage,
                    });
                  }
                }
              }
            });
          }
          processGarment(FibrefilteredData, value.headerList);

          const garmentData2 = {
            garment: [
              {
                garment: "",
              },
            ],
            percentage: 0,
            isSave: false,
            isEdit: false,
            fibres: [
              {
                fibre: "",
                percentage: 0,
              },
            ],
          };
          _formData[value.fieldName].push(garmentData2);
        } else {
          if (formData && formData[value.fieldName]) {
            _formData[value.fieldName] = formData[value.fieldName];
          } else {
            _formData[value.fieldName] = [
              {
                garment: [
                  {
                    garment: "",
                  },
                ],
                percentage: 0,
                isSave: false,
                isEdit: false,
                fibres: [
                  {
                    fibre: "",
                    percentage: 0,
                  },
                ],
              },
            ];
          }
        }
      } else if (value.prefix === "CCL") {
        _tableData = {};
        _formData[value.fieldName] = [];
        for (var i = 0; i < value.numberOfLinesToDisplay; i++) {
          for (var value1 of Object.values(value.careControls)) {
            if (value1.prefix === "CHK") {
              _tableData[value1.fieldName] = "N";
            } else {
              _tableData[value1.fieldName] = "";
            }
          }
          _formData[value.fieldName].push(_tableData);
        }
      } else {
        if (copyOrder && !uploadedFileTesco) {
          const data = copyOrderData.Order[0].VIPSOrderDetailItem;
          copyCheckoutData(copyOrderData, user_id, searchParams);
          const filteredData = data.filter((item) => {
            const productIdParts = item.original_product_id
              ? item.original_product_id.split("[")
              : item.product_id.split("[");
            const productIdFirstPart = productIdParts[0].trim();
            return (
              productIdFirstPart === effectiveLabelRef ||
              item.VIPSVarOrderDetailItem.length > 0
            );
          });
          const variableTextToSearch = value.tagName + "_1";
          if (filteredData) {
            const variableDesc = getVariableDesc(
              filteredData[0].VIPSVarOrderDetailItem,
              variableTextToSearch
            );
            _formData[value.fieldName] = variableDesc;
            if (value.prefix === "QTY") {
              _formData["INQ" + value.tagName] = getVariableDesc(
                filteredData[0].VIPSVarOrderDetailItem,
                "INQ" + variableTextToSearch
              );
            }
          } else {
            _formData[value.fieldName] = "";
          }
        } else {
          if (tescoFiledMapping) {
            processCostDetails2(
              uploadedFileTesco,
              value,
              _formData,
              _tableData,
              tescoFiledMapping,
              details,
              sizeChart,
              fileType,
              orderType
            );
          } else {
            _formData[value.fieldName] = "";
          }
        }
      }
    }
    setFormData(_formData);
  });

};

// ---------------------------------------------------------------
// CSV Parsing and Combining

export const parseCSV = (csvText) => {
  const rows = csvText
    .trim()
    .split("\n")
    .filter((row) => row.trim() !== ""); // Trim and filter out empty rows

  // Extract column headers
  //let headers = rows[0].split(";");
  let headers = rows[0]
    .split(";")
    .map((header) => header.trim().replace(/\r/g, ""));

  // Initialize an object to store mapped values for split headers
  let mappedValues = {};

  // Initialize dataRows array
  let dataRows = [];

  // Iterate over each row's data
  rows.slice(1).forEach((row, rowIndex) => {
    const values = row.split(";");
    const rowData = {};

    values.forEach((value, index) => {
      const header = headers[index];
      if (value.length > 250) {
        // Split the value into chunks of 250 characters or less
        splitValue(value, rowIndex, header, mappedValues);
        // Update original value with trimmed value
        value = value.substring(0, 250);
      }
      rowData[header] = value !== "" ? value : null;
    });

    dataRows.push(rowData); // Push rowData into dataRows array
  });

  // Merge values from mappedValues into data
  Object.entries(mappedValues).forEach(([header, values]) => {
    values.forEach(({ value, rowIndex }) => {
      const rowData = dataRows[rowIndex];
      if (!rowData) {
        dataRows[rowIndex] = {};
      }
      // Check if the header exists in the row, otherwise create it
      if (!rowData[header]) {
        rowData[header] = value !== "" ? value : null;
      } else {
        // If the header already exists, append the value
        rowData[header] = value !== "" ? value : null;
      }
    });
  });

  // Generate modified headers based on mapped values
  const modifiedHeaders = headers.flatMap((header, _index) => {
    const levels =
      Object.keys(mappedValues).filter((key) => key.startsWith(`${header}`))
        .length + 1;
    if (levels > 1) {
      return [...Array(levels - 1).keys()].map((i) =>
        i === 0 ? header : `${header}-${i + 1}`
      ); // Show only the original header if i = 0
    }
    return header;
  });

  return {
    headers: modifiedHeaders,
    data: dataRows,
    mappedValues: mappedValues,
  };
};

export const combineCSVData = (csvText1, csvText2) => {
  const parsedData1 = parseCSV(csvText1);
  const parsedData2 = parseCSV(csvText2);

  // Combine headers from both CSVs
  const combinedHeaders = [
    ...new Set([...parsedData1.headers, ...parsedData2.headers]),
  ];

  // Try to find PO_NUMBER or ORDER Nº column for PO-based matching
  const po_index_csv1 = parsedData1.headers.indexOf("PO_NUMBER") >= 0 
    ? parsedData1.headers.indexOf("PO_NUMBER") 
    : parsedData1.headers.indexOf("ORDER Nº");
  const po_index_csv2 = parsedData2.headers.indexOf("PO_NUMBER");

  // If PO columns don't exist in both CSVs, fall back to row-by-row merge (legacy behavior)
  const hasPOMatching = po_index_csv1 >= 0 && po_index_csv2 >= 0;

  let combinedData = [];
  let matchedPOs = [];

  if (hasPOMatching) {
    // PO-based matching logic for ISI scenario

    // Group ISI rows by PO_NUMBER
    const isiRowsByPO = {};
    parsedData2.data.forEach((row) => {
      const po = row[parsedData2.headers[po_index_csv2]]?.toString().trim();
      if (po) {
        if (!isiRowsByPO[po]) {
          isiRowsByPO[po] = [];
        }
        isiRowsByPO[po].push(row);
      }
    });

    // Count main rows per PO for validation
    const mainRowCountByPO = {};
    parsedData1.data.forEach((row) => {
      const po = row[parsedData1.headers[po_index_csv1]]?.toString().trim();
      if (po && isiRowsByPO[po]) {
        mainRowCountByPO[po] = (mainRowCountByPO[po] || 0) + 1;
      }
    });

    // Validate: main row count must equal ISI row count for each matching PO
    for (const po of Object.keys(mainRowCountByPO)) {
      const mainCount = mainRowCountByPO[po];
      const isiCount = isiRowsByPO[po].length;
      if (mainCount !== isiCount) {
        return {
          error: `PO ${po}: main file has ${mainCount} row(s) but ISI file has ${isiCount} row(s). Counts must match.`,
          headers: combinedHeaders,
          data: [],
          matchedPOs: [],
        };
      }
    }

    // Track which ISI row to use for each matching PO (sequential, counts already validated equal)
    const isiIndexByPO = {};

    // Merge main rows with matching ISI rows
    parsedData1.data.forEach((mainRow) => {
      const mainPO = mainRow[parsedData1.headers[po_index_csv1]]?.toString().trim();
      const combinedRow = {};

      // First, add all main row values
      combinedHeaders.forEach((header) => {
        const value = mainRow[header];
        combinedRow[header] = value !== undefined ? value : null;
      });

      // Then, merge with matching ISI row if PO matches
      if (mainPO && isiRowsByPO[mainPO]) {
        // Track matched PO
        if (!matchedPOs.includes(mainPO)) {
          matchedPOs.push(mainPO);
        }

        // Get current index for this PO (sequential, no cycling needed - counts are equal)
        if (isiIndexByPO[mainPO] === undefined) {
          isiIndexByPO[mainPO] = 0;
        }
        const isiRow = isiRowsByPO[mainPO][isiIndexByPO[mainPO]];
        isiIndexByPO[mainPO]++;

        // Merge ISI row values (prefer ISI data for matching columns)
        combinedHeaders.forEach((header) => {
          const isiValue = isiRow[header];
          if (isiValue !== undefined && isiValue !== null) {
            combinedRow[header] = isiValue;
          }
        });
      } else if (mainPO) {
        // Non-matching PO: leave ISI columns as null
        parsedData2.headers.forEach((isiHeader) => {
          if (!parsedData1.headers.includes(isiHeader)) {
            combinedRow[isiHeader] = null;
          }
        });
      }

      combinedData.push(combinedRow);
    });
  } else {
    // Legacy row-by-row merge for cases without PO_NUMBER column
    const maxRows = Math.max(parsedData1.data.length, parsedData2.data.length);

    for (let i = 0; i < maxRows; i++) {
      const row1 = parsedData1.data[i] || {};
      const row2 = parsedData2.data[i] || {};

      const combinedRow = {};

      combinedHeaders.forEach((header) => {
        const value1 = row1[header] || null;
        const value2 = row2[header] || null;

        if (value1 !== null && value2 !== null) {
          combinedRow[header] = `${value1}`;
        } else if (value1 !== null) {
          combinedRow[header] = value1;
        } else if (value2 !== null) {
          combinedRow[header] = value2;
        }
      });

      combinedData.push(combinedRow);
    }
  }

  const combinedMappedValues = {
    ...parsedData1.mappedValues,
    ...parsedData2.mappedValues,
  };

  return {
    headers: combinedHeaders,
    data: combinedData,
    mappedValues: combinedMappedValues,
    matchedPOs: hasPOMatching ? matchedPOs : undefined, // Only return matchedPOs when using PO-based matching
  };
};

// ---------------------------------------------------------------
// Session and Local Storage

export async function SaveSessionData(user_id, jsonObjectId) {
  var saveUser = "SU" + jsonObjectId + user_id + "saveUser";
  var saveSchemaObject = "SO" + jsonObjectId + user_id + "saveSchemaObject";
  var userOEForm = user_id + jsonObjectId + "userOEForm";
  var firstPageId = "firstPageID";
  var multiSelectLabelItem = user_id + jsonObjectId + "multiSelectLabelItem";
  var checkoutForm = user_id + jsonObjectId + "checkoutForm";
  var autocomplete = user_id + jsonObjectId + "autocomplete";
  var uniqueLabelRef1 = user_id + jsonObjectId + "uniqueLabelRef1";

  const filteredData = sessionCache.toFilteredObject([
    saveUser,
    saveSchemaObject,
    userOEForm,
    firstPageId,
    multiSelectLabelItem,
    checkoutForm,
    autocomplete,
    uniqueLabelRef1,
  ]);

  const saveLabelSessionKey = user_id + "saveLabelSession";
  const saveLabelSessionRaw = localStorage.getItem(saveLabelSessionKey);
  if (saveLabelSessionRaw) {
    try { filteredData[saveLabelSessionKey] = JSON.parse(saveLabelSessionRaw); } catch (_) {}
  }

  const dataToPersist =
    Object.keys(filteredData).length > 0 ? filteredData : sessionCache.toObject();

  await saveUserSessionData(user_id, jsonObjectId, dataToPersist);
}
export async function saveSchemaObjectLocal(
  sessionArray,
  labelSessionArray,
  searchParams,
  val2
) {
  let user_id = "";
  sessionArray.forEach(function (item) {
    if (item.Key == "user_id") {
      user_id = item.Value;
    }
  });
  let newArray = labelSessionArray.map((element) => {
    let parts = element.split("[");
    return parts[0]; // Removes the trailing ']'
  });

  var index = newArray.indexOf(searchParams.get("label")); // 1
  index = index + 1;
  const sessionObjId = getSessionObjectId(searchParams, labelSessionArray);
  sessionCache.set("SO" + sessionObjId + user_id + "saveSchemaObject", val2);

  await SaveSessionData(user_id, sessionObjId);

}

// ---------------------------------------------------------------
// Fetch and Navigation Helpers

export const fetchResult2 = async (
  pageName,
  searchParams,
  setPageId,
  setLabelsCount2,
  setIsLoading
) => {
  let results = await GetSchemaPageIdFromLabelType(
    pageName,
    searchParams.get("schema_id"),
    setPageId,
    setLabelsCount2,
    setIsLoading
  );

  const data = await results;
  return data;
};

export async function newFunction(
  labelSessionArray,
  searchParams,
  setPageId,
  setLabelsCount2,
  setIsLoading,
  user_id,
  labelsCount2,
  pageSequence,
  dispatch,
  LabelSession,
  setlabelReference
) {
  if (labelSessionArray.length > 0) {
    var selectedLabel = labelSessionArray[0].split("[");
    var pageName = selectedLabel[1].replace("]", "");
    GetFirstPageIdFromLabelType(
      pageName,
      searchParams.get("schema_id"),
      setIsLoading
    );
    if (pageName === searchParams.get("label") && searchParams.get("OCC")) {
      setLabelsCount2(0);
    } else if (pageSequence === 0) {
      //localStorage.removeItem("firstPageID");
      localStorage.removeItem("firstLabel");
    }
    var lblCount2 = labelSessionArray.length - 1;
    if (
      labelSessionArray.length > 0 &&
      labelsCount2 <= labelSessionArray.length &&
      searchParams.get("OCC")
    ) {
      lblCount2 = lblCount2 ?? labelsCount2;

      let newArray = labelSessionArray.map((element) => {
        let parts = element.split("[");
        return parts[0]; // Removes the trailing ']'
      });

      localStorage.setItem(
        user_id + "saveLabelSession",
        JSON.stringify({ LabelSession: newArray })
      );
//if (searchParams.get("label") === null || searchParams.get("OCC")) {
              SaveSessionData(
          user_id,
          getSessionObjectId(searchParams, labelSessionArray)
        );
// } else {
//         SaveSessionDataForLabel(
//           user_id,
//           searchParams.get("label") !== null
//             ? searchParams.get("label")
//             : searchParams.get("PageID")
//         );
//       }
      
      dispatch(
        LabelSession.actions.saveLabelSession({
          LabelSession: newArray,
        })
      );

      var selectedLabel = labelSessionArray[lblCount2].split("[");
      var labelType = selectedLabel[1].replace("]", "");
      var labelRef = selectedLabel[0].replace("]", "");
      setlabelReference(labelRef);

      await GetSchemaPageIdFromLabelType(
        labelType,
        searchParams.get("schema_id"),
        setPageId,
        setLabelsCount2,
        setIsLoading
      );
    }
  }
}

// utility function to create query string
const createQueryString = (searchParams, pageID, labelRef, labelsCount2) => {
  return `&session_id=${searchParams.get(
    "session_id"
  )}&PageID=${pageID}&schema_id=${searchParams.get(
    "schema_id"
  )}&store_grp=${searchParams.get(
    "store_grp"
  )}&store_grp_desc=${searchParams.get(
    "store_grp_desc"
  )}&vd_mode=${searchParams.get(
    "vd_mode"
  )}&label=${labelRef}&pageCount=${labelsCount2}&OCC=True&vipsOrder=Inprogress`;
};

// function to handle fetch result
export const handleFetchResult = (
  //fetchResult,
  searchParams,
  _user,
  setVal,
  setVal2,
  setTableItemID,
  setFormData,
  initForm,
  setPageId,
  setPageSequence,
  setChildPages,
  setIsLoading,
  setSchemaNotFound,
  setDisableByLabelName,
  setMandatoryByLabelName,
  setSelectedOption,
  setMapTableItemID,
  user_id,
  uniqueProductIds,
  copyOrderData,
  labelReference,
  _navigate,
  pageSequence,
  setlabelReference,
  labelSessionArray,
  setLabelsCount2,
  labelsCount2,
  dispatch,
  LabelSession,
  divisionId,
  setSchemaData
) => {
    fetchResult(
    labelSessionArray,
    searchParams,
    setPageId,
    setLabelsCount2,
    setIsLoading,
    user_id,
    labelsCount2,
    pageSequence,
    dispatch,
    LabelSession,
    setlabelReference,
    divisionId,
    setSchemaData
  )
    .then((data) => {
            if (data.schemaPageFields.length <= 0) {
        setSchemaNotFound(true);
      }

      // Replace FACTORY SLI field options with GetFactoryCodes API data
      // Read factory/division tag names from config (system param REACT_APP_FACTORY_DIVISION_SCHEMAS)
      let factoryTagName = "";
      let divisionTagName = "";
      try {
        const fdConfig = JSON.parse(localStorage.getItem("factoryDivisionConfig"));
        if (fdConfig && fdConfig.factoryTagName) factoryTagName = fdConfig.factoryTagName;
        if (fdConfig && fdConfig.divisionTagName) divisionTagName = fdConfig.divisionTagName;
      } catch (_) {}
      const factoryField = factoryTagName
        ? data.schemaPageFields.find(
            (field) => field.prefix === "SLI" && field.tagName === factoryTagName
          )
        : null;
      if (factoryField) {
        // Clear division options so it renders empty until factory is selected
        if (divisionTagName) {
          const divisionField = data.schemaPageFields.find(
            (field) => field.prefix === "SLI" && field.tagName === divisionTagName
          );
          if (divisionField) {
            divisionField.options = [];
          }
        }
        const storeGrp = searchParams?.get?.("store_grp") || "";
        // Read user_type from sessionArray to conditionally pass division/store params
        let userType = "";
        try {
          const sa = JSON.parse(localStorage.getItem("sessionArray")) || [];
          const utItem = sa.find((it) => it.Key === "user_type");
          if (utItem) userType = (utItem.Value || "").trim();
        } catch (_) {}
        GetFactoryCodes(user_id, userType, divisionId, storeGrp)
          .then((factoryCodes) => {
            if (Array.isArray(factoryCodes) && factoryCodes.length > 0) {
              factoryField.options = factoryCodes;
            }
            setVal([...data.schemaPageFields]);
          })
          .catch(() => {
            setVal(data.schemaPageFields);
          });
      } else {
        setVal(data.schemaPageFields);
      }

      //setIsLoading(false);

      const labField = data.schemaPageFields.find(
        (field) => field.prefix === "LAB" && field.multiSelect === false
      );
      const validation = labField ? labField.validation : null;

      if (validation && validation.LableTableMapPart) {
        const parts = validation.LableTableMapPart.split("|");
        const values = parts
          .map((part) => part.split("#")[1])
          .filter((value) => value);
        setTableItemID(values);
      } else {
        //console.log("LableTableMapPart data not found.");
      }
  
      setVal2((current) => [...current, data.schemaPageFields]);

      const userKey =
        "SU" +
        getSessionObjectId(searchParams, labelSessionArray) +
        user_id +
        "saveUser";
      const test = sessionCache.get(userKey) ?? null;

      if (test !== null) {
        const parsedTest = test;
        const labelRefValue = parsedTest[labField?.fieldName];
        setDisableByLabelName(labelRefValue?.split("[")[0]);
        setMandatoryByLabelName(labelRefValue?.split("[")[0]);
        setSelectedOption(labelRefValue);

        const partWithDropdownValue = validation?.LableTableMapPart?.split(
          "|"
        ).find((part) => part.startsWith(labelRefValue?.split("[")[0]));
        const number = labelRefValue
          ? partWithDropdownValue?.split("#")[1]
          : null;

        setMapTableItemID(number);
        setFormData(parsedTest);
      } else {
        // For copy orders, wrap setFormData to also persist to session so the
        // data survives subsequent navigations without re-fetching the order.
        const wrappedSetFormData = searchParams.get("OrderID")
          ? (formDataVal) => {
              setFormData(formDataVal);
              const sessionObjId = getSessionObjectId(searchParams, labelSessionArray);
              if (sessionObjId) {
                const suKey = "SU" + sessionObjId + user_id + "saveUser";
                sessionCache.set(suKey, formDataVal);
                SaveSessionData(user_id, sessionObjId);
              }
            }
          : setFormData;

        initForm(
          "",
          searchParams,
          data.schemaPageFields,
          uniqueProductIds,
          searchParams.get("OrderID"),
          copyOrderData,
          labelReference,
          user_id,
          wrappedSetFormData
        );
      }

      setPageId(data?.pageId);
      setPageSequence(data?.pageSequence);
      if (data?.pageSequence === 1) {
        setChildPages(data?.childPages);
        // persist childPages for use in checkout
        try {
          localStorage.setItem("childPages", JSON.stringify(!!data?.childPages));
        } catch {}
      }
      // defer turning off global loading until after React’s initial render
      setTimeout(() => setIsLoading(false), 0);
    })
    .catch((error) => console.error(error));
};

// main function to execute logic
export const executeLogic = (
  searchParams,
  fetchResult2,
  _fetchResult,
  pageName,
  user,
  setVal,
  setVal2,
  setTableItemID,
  setFormData,
  initForm,
  setPageId,
  setPageSequence,
  setChildPages,
  setIsLoading,
  setSchemaNotFound,
  setDisableByLabelName,
  setMandatoryByLabelName,
  setSelectedOption,
  setMapTableItemID,
  labelRef,
  labelsCount2,
  labelSessionArray,
  user_id,
  copyOrderData,
  labelReference,
  divisionId,
  VIPSDataServiceURL,
  requestOptions,
  navigate,
  uniqueProductIds,
  pageSequence,
  setlabelReference,
  setLabelsCount2,
  dispatch,
  LabelSession,
  setSchemaData,
  setCopyOrderData
) => {
  if (searchParams.get("label") === null && searchParams.get("OCC")) {
    fetchResult2(
      pageName,
      searchParams,
      setPageId,
      setLabelsCount2,
      setIsLoading
    ).then((pageID) => {
      navigate({
        pathname: "/" + process.env.REACT_APP_ORDERING_PATH,
        search: createQueryString(searchParams, pageID, labelRef, labelsCount2),
      });
    });
  } else if (searchParams.get("OrderID")) {
    const URL = `${VIPSDataServiceURL}/Order/GetOrderInformation?divisionID=${divisionId}&orderIDList=${searchParams.get(
      "OrderID"
    )}`;
    fetch(URL, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        setCopyOrderData(data);
        const uniqueProductIds = [
          ...new Set(
            data.Order.flatMap((order) =>
              order.VIPSOrderDetailItem.filter(
                (item) => !item.Additional_Line_Flag
              )
                .map((item) =>
                  item.original_product_id
                    ? item.original_product_id.trim()
                    : item.product_id
                )
                .filter((id) => id !== "")
            )
          ),
        ];
        handleFetchResult(
          //fetchResult,
          searchParams,
          user,
          setVal,
          setVal2,
          setTableItemID,
          setFormData,
          initForm,
          setPageId,
          setPageSequence,
          setChildPages,
          setIsLoading,
          setSchemaNotFound,
          setDisableByLabelName,
          setMandatoryByLabelName,
          setSelectedOption,
          setMapTableItemID,
          user_id,
          uniqueProductIds,
          data,
          labelReference,
          navigate,
          pageSequence,
          setlabelReference,
          labelSessionArray,
          setLabelsCount2,
          labelsCount2,
          dispatch,
          LabelSession,
          divisionId,
          setSchemaData
        );
      })
      .catch((error) => console.error(error));
  } else {
    handleFetchResult(
      //fetchResult,
      searchParams,
      user,
      setVal,
      setVal2,
      setTableItemID,
      setFormData,
      initForm,
      setPageId,
      setPageSequence,
      setChildPages,
      setIsLoading,
      setSchemaNotFound,
      setDisableByLabelName,
      setMandatoryByLabelName,
      setSelectedOption,
      setMapTableItemID,
      user_id,
      uniqueProductIds,
      copyOrderData,
      labelReference,
      navigate,
      pageSequence,
      setlabelReference,
      labelSessionArray,
      setLabelsCount2,
      labelsCount2,
      dispatch,
      LabelSession,
      divisionId,
      setSchemaData
    );
  }
};

export const fetchResult = async (
  labelSessionArray,
  searchParams,
  setPageId,
  setLabelsCount2,
  setIsLoading,
  user_id,
  labelsCount2,
  pageSequence,
  dispatch,
  LabelSession,
  setlabelReference,
  divisionId,
  setSchemaData
) => {
    if (searchParams.get("OCC")) {
    await newFunction(
      labelSessionArray,
      searchParams,
      setPageId,
      setLabelsCount2,
      setIsLoading,
      user_id,
      labelsCount2,
      pageSequence,
      dispatch,
      LabelSession,
      setlabelReference
    );
  }
  return await GetSchemaPageItems(
    searchParams.get("PageID"),
    searchParams.get("schema_id"),
    divisionId,
    user_id,
    setSchemaData,
    searchParams.get("label"),
    searchParams.get("labelType"),
  );
};

// ---------------------------------------------------------------
// Data Filtering

export function getFilteredData(firstArray, secondArray, relatedData) {
  const result = {};

  secondArray[0].forEach((item) => {
    if (firstArray.includes(item.tabValue.toString())) {
      const fieldName = item.fieldName;
      if (relatedData[fieldName]) {
        result[fieldName] = relatedData[fieldName];
      }
    }
  });
  return result;
}

// ---------------------------------------------------------------
// File Upload Handlers

export const handleUpload = async ({
  file1,
  file2,
  setIsLoading,
  setMapTableItemID,
  setErrorMessage,
  setSnackbarMessage,
  setOpenSnackbar,
  setUploaded,
  setCsvData,
  setCombinedData,
  setFile1,
  setFile2,
  fileInput1Ref,
  fileInput2Ref,
  combineCSVData,
  initForm,
  regexString,
  searchParams,
  schemaData,
  uniqueProductIds,
  copyOrderData,
  labelReference,
  user_id,
  setFormData,
  userSelection,
  schemaName,
}) => {
  setIsLoading(true);
  setMapTableItemID && setMapTableItemID(null);

  if (!file1 || !file2) {
    alert("Please upload both files");
    setIsLoading(false);
    return;
  }
  if (!file1.name.endsWith(".csv") || !file2.name.endsWith(".csv")) {
    setErrorMessage && setErrorMessage("Please upload a CSV file.");
    setIsLoading(false);
    return;
  }

  // Helper to read file as text
  const fileToText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Read the files as text
  const text1 = await fileToText(file1);
  const text2 = await fileToText(file2);

  // Combine the data from both CSVs
  const combined = combineCSVData(text1, text2);

  // If validation failed, show as alert bar and abort
  if (combined.error) {
    if (setSnackbarMessage && setOpenSnackbar) {
      setSnackbarMessage(combined.error);
      setOpenSnackbar(true);
    } else {
      setErrorMessage && setErrorMessage(combined.error);
    }
    // Clear uploaded files so user must re-upload corrected CSVs
    setFile1 && setFile1(null);
    setFile2 && setFile2(null);
    if (fileInput1Ref && fileInput1Ref.current) {
      fileInput1Ref.current.value = null;
    }
    if (fileInput2Ref && fileInput2Ref.current) {
      fileInput2Ref.current.value = null;
    }
    setIsLoading(false);
    return;
  }

  setCombinedData && setCombinedData(combined);
  setCsvData && setCsvData(combined);
  setFile1 && setFile1(null);
  setFile2 && setFile2(null);
  if (fileInput1Ref && fileInput1Ref.current) {
    fileInput1Ref.current.value = null;
  }
  if (fileInput2Ref && fileInput2Ref.current) {
    fileInput2Ref.current.value = null;
  }
  initForm(
    regexString,
    searchParams,
    schemaData.schemaPageFields,
    uniqueProductIds,
    searchParams.get("OrderID"),
    copyOrderData,
    labelReference,
    user_id,
    setFormData,
    combined,
    userSelection,
    schemaName
  );
  setErrorMessage && setErrorMessage("");
  setUploaded && setUploaded(true);
  setIsLoading(false);
};

export const handleFileUpload = ({
  e,
  userSelection,
  setIsLoading,
  setMapTableItemID,
  setErrorMessage,
  setCsvData,
  setUploaded,
  initForm,
  regexString,
  searchParams,
  schemaData,
  uniqueProductIds,
  copyOrderData,
  labelReference,
  user_id,
  setFormData,
  schemaName,
}) => {
  if (!userSelection) {
    setIsLoading(true);
    setMapTableItemID(null);
    const file = e.target.files[0];
    const reader = new FileReader();
    e.target.value = null;
    // Check if the file format is CSV
    if (!file.name.endsWith(".csv")) {
      setErrorMessage("Please upload a CSV file.");
      setIsLoading(false);
      return;
    }

    reader.onload = (event) => {
      const text = event.target.result;
      const data = parseCSV(text);
      if (data.headers.length === 0) {
        setErrorMessage(
          "No data found in the CSV file or required columns are missing."
        );
        setCsvData({});
        setUploaded(false);
      } else {
        setCsvData(data);
        initForm(
          regexString,
          searchParams,
          schemaData.schemaPageFields,
          uniqueProductIds,
          searchParams.get("OrderID"),
          copyOrderData,
          labelReference,
          user_id,
          setFormData,
          data,
          userSelection,
          schemaName
        );
        setErrorMessage("");
        setUploaded(true);
      }
      setIsLoading(false);
    };
    reader.readAsText(file);
  }
};

// ---------------------------------------------------------------
// Form Submission

export const onSubmit = async ({
  values,
  csvUplaodEnabled,
  selectedTabs,
  setSnackbarMessage,
  getMessage,
  setOpenSnackbar,
  getFilteredData,
  setResetELCList,
  sessionArray,
  pageSequence,
  childPages,
  val,
  setLabels,
  setLabelsCount,
  labelsCount,
  labels,
  labelArray2,
  dispatch,
  LabelSession,
  objetArray,
  user,
  userFields,
  saveUserLocal,
  searchParams,
  setObjetArray,
  setPageName,
  removeSeperatorValue,
  TableType,
  SchemaObject,
  saveSchemaObjectLocal,
  labelSessionArray,
  val2,
  setIsLoading,
  navigate,
  createSearchParams,
  location,
  setlabelReference,
  VIPSDataServiceURL,
  requestOptions,
  setPageId,
  ediReadonlyMode,
}) => {
  if (csvUplaodEnabled && selectedTabs.length <= 0) {
    setSnackbarMessage(getMessage("orderingOption"));
    setOpenSnackbar(true);
    return;
  }
 

  const filtered = Object.fromEntries(
    Object.entries(values).filter(([_, value]) => value !== '' && value !== null)
  );
    let _values = filtered;
  if (csvUplaodEnabled) {
        const filteredData = getFilteredData(selectedTabs, val2, values);
    _values = filteredData;
  }
  setResetELCList(false);
  let user_id = "";
  sessionArray.forEach(function (item) {
    if (item.Key == "user_id") {
      user_id = item.Value;
    }
  });
  let labelArray;
  let lblCount;
  var atLeastOneLabelSelected = 0;
  var hasLabel = false;
  if (pageSequence === 1 && childPages) {
    let labelSelectionPageFields = [];
    val.forEach((schema, _index) => {
      if (schema.prefix === "LAB" && _values[schema.fieldName]) {
        labelSelectionPageFields.push(_values[schema.fieldName]);
      }
    });
        setLabels(labelSelectionPageFields);
    labelArray = labelSelectionPageFields;
    setLabelsCount(0);
    lblCount = 0;
  }
  lblCount = lblCount ?? labelsCount;
  labelArray = labelArray ?? labels;
  let labelArray3 = Array.isArray(labelArray2) ? [...labelArray2] : [];
  let testArray1 = [];
  const filteredArray = labelArray.filter((item) => item.trim() !== "");
  const toFindDuplicates = (arr) => {
    const duplicates = arr.filter((item, index) => arr.indexOf(item) !== index);
    return duplicates;
  };
  const duplicateElements = toFindDuplicates(filteredArray);

  // Validate selected label against API before navigating to checkout
  async function validateLabelBeforeCheckout() {
    try {
      const storeGroup = searchParams.get("store_grp");
      let divisionId = "";
      sessionArray.forEach(function (item) {
        if (item.Key === "division_id") {
          divisionId = item.Value;
        }
      });
      // Override divisionId from factory config when available
      try {
        const fdConfig = JSON.parse(localStorage.getItem("factoryDivisionConfig"));
        if (fdConfig && fdConfig.divisionTagName) {
          let overrideFound = false;
          // Read from sessionCache (session data no longer lives in localStorage)
          const sessionObjId = getSessionObjectId(searchParams, labelSessionArray);
          const soKey = "SO" + sessionObjId + user_id + "saveSchemaObject";
          const suKey = "SU" + sessionObjId + user_id + "saveUser";
          const schemaPages = sessionCache.get(soKey) ?? null;
          const userData = sessionCache.get(suKey) ?? null;
          if (schemaPages && userData) {
            const allFields = Array.isArray(schemaPages[0]) ? schemaPages.flat() : schemaPages;
            const divField = allFields.find(
              (f) => f && f.tagName === fdConfig.divisionTagName && f.prefix === "SLI"
            );
            if (divField) {
              const rawValue = userData[divField.fieldName];
              if (rawValue && typeof rawValue === "string") {
                const parts = rawValue.split("||");
                divisionId = parts.length > 1 ? parts[1].trim() : rawValue.trim();
                overrideFound = true;
              }
            }
          }
          // Fallback: read directly from in-memory schema (val) and form values
          // This handles the first click when localStorage hasn't been saved yet
          if (!overrideFound && Array.isArray(val)) {
            const allInMemory = Array.isArray(val[0]) ? val.flat() : val;
            const divFieldMem = allInMemory.find(
              (f) => f && f.tagName === fdConfig.divisionTagName && f.prefix === "SLI"
            );
            if (divFieldMem) {
              const rawValue = values[divFieldMem.fieldName] || _values?.[divFieldMem.fieldName];
              if (rawValue && typeof rawValue === "string") {
                const parts = rawValue.split("||");
                divisionId = parts.length > 1 ? parts[1].trim() : rawValue.trim();
              }
            }
          }
        }
      } catch (_) {}
      // Collect all label refs from current form or localStorage, deduped
      let labelRefs = [];
      try {
        if (Array.isArray(val)) {
          for (const schema of val) {
            if (schema.prefix === "LAB") {
              const v = _values?.[schema.fieldName];
              if (typeof v === "string" && v.trim() !== "") {
                const ref = v.includes("[") ? v.split("[")[0]?.replace("]", "") : v;
                if (ref) labelRefs.push(ref);
              } else if (Array.isArray(v)) {
                v.forEach((vv) => {
                  if (typeof vv === "string" && vv.trim() !== "") {
                    const ref = vv.includes("[") ? vv.split("[")[0]?.replace("]", "") : vv;
                    if (ref) labelRefs.push(ref);
                  }
                });
              }
            }
          }
        }
        // Fallback: use uniqueLabelRef1 from session cache
        if (labelRefs.length === 0) {
          const compoundKey =
            user_id +
            getSessionObjectId(searchParams, labelSessionArray) +
            "uniqueLabelRef1";
          const uniqueLabelRef1Array = sessionCache.get(compoundKey);
          if (Array.isArray(uniqueLabelRef1Array) && uniqueLabelRef1Array.length > 0) {
            labelRefs.push(...uniqueLabelRef1Array);
          }
        }
      } catch (_ignore) {}

      // Deduplicate and validate inputs
      labelRefs = Array.from(new Set(labelRefs.filter((x) => typeof x === "string" && x.trim() !== "")));
      if (!storeGroup || !divisionId || labelRefs.length === 0) {
        return true; // missing context: do not block
      }

      // Validate all labels in parallel
      const results = await Promise.all(
        labelRefs.map(async (lr) => {
          const url = `${VIPSDataServiceURL}/Product/IsLabelRefConfigured?storeGroup=${encodeURIComponent(storeGroup)}&labelRef=${encodeURIComponent(lr)}&divisionId=${encodeURIComponent(divisionId)}`;
          const resp = await fetch(url, requestOptions);
          const ok = await resp.json();
          return { lr, ok: !!ok };
        })
      );

      const invalidLabels = results.filter((r) => r.ok === false).map((r) => r.lr);
      if (invalidLabels.length > 0) {
        const msg = getMessage ? getMessage("labelOrderingClosed") : "Ordering closed";
        const finalMsg = typeof msg === "string" ? msg.replace("{Label}", invalidLabels.join(", ")).replace("{DivisionId}", divisionId) : "Ordering closed";
        setSnackbarMessage(finalMsg);
        setOpenSnackbar(true);
        return false;
      }
      return true;
    } catch (_e) {
      // On error, do not block user flow
      return true;
    }
  }
  // Helper function to process schema labels and create label objects
  function processSchemaLabels(val, filteredArray, _values, labelArray2, pageSequence, childPages, dispatch, LabelSession) {
    let labelArray3 = [...labelArray2];
    let pageName;

    val.forEach((schema, index) => {
      if (filteredArray[index] != undefined) {
        const pageFields = Object.values(_values);
        if (pageFields[index]) {
          const selectedLabel = filteredArray[index].split("[");
          pageName = selectedLabel[1].replace("]", "");
          const labelRef = selectedLabel[0].replace("]", "");

          const labelObj = {
            fieldName: schema.fieldName,
            tagName: schema.tagName,
            prefix: schema.prefix,
            labelRef,
          };

          if (pageSequence === 1 && childPages) {
            labelArray3 = [...labelArray3, labelObj];
            labelArray3 = labelArray3 ?? labelArray2;
            dispatch(
              LabelSession.actions.saveLabelSession({
                LabelSession: labelArray3,
              })
            );
          }
        }
      }
    });
    
    return pageName;
  }

  // Helper function to save user data and update state
  function saveUserDataAndState(
    _values, 
    user, 
    _userFields, 
    objetArray, 
    setObjetArray, 
    setLabelsCount, 
    labelsCount, 
    dispatch, 
    saveUserLocal, 
    user_id, 
    searchParams
  ) {
    const testArray1 = objetArray;
    const user1 = { ...user, ..._values };
    
    setLabelsCount(labelsCount + 1);
    setObjetArray((current) => [...current, _values]);

    dispatch(userSlice.actions.saveUser(testArray1));
    saveUserLocal({ ..._values });

    sessionCache.set(
      user_id + getSessionObjectId(searchParams, labelSessionArray) + "userOEForm",
      { ..._values }
    );
    
    return user1;
  }

  // Helper function to process static values for schemas
  function processStaticValues(val, _values, TableType, csvUplaodEnabled, ediReadonlyMode) {
    function addStaticValue(schema, tableName) {
      if (schema.prefix === "STC" && !csvUplaodEnabled && !ediReadonlyMode) {
        for (const data of _values[tableName]) {
          if (Object.values(data).some((x) => x?.length > 0)) {
            data[schema.fieldName] = schema.value;
          }
        }
      }
    }

    for (const schema of val) {
      if (schema.dataType === TableType) {
        for (const data of schema.schemaPageSubItems) {
          removeSeperatorValue(data, schema.fieldName, _values);
          addStaticValue(data, schema.fieldName);
        }
      } else {
        removeSeperatorValue(schema);
      }
    }
  }

  // Helper function to navigate to next page
  async function navigateToNextPage(
    filteredArray, 
    lblCount, 
    setPageName, 
    saveSchemaObjectLocal, 
    sessionArray, 
    labelSessionArray, 
    searchParams, 
    val2, 
    GetPageID
  ) {
    const selectedLabel2 = filteredArray[lblCount].split("[");
    const pageName2 = selectedLabel2[1].replace("]", "");
    const labelName = selectedLabel2[0].replace("]", "");
    
    setPageName(pageName2);
    
    const promise = saveSchemaObjectLocal(
      sessionArray,
      labelSessionArray,
      searchParams,
      val2
    );
    
    promise.then(async (_response) => {
      await GetPageID(pageName2, labelName);
    });
  }

  // Helper function to handle API navigation
  async function handleApiNavigation(
    filteredArray, 
    lblCount, 
    _values,
    val,
    labelArray2,
    pageSequence,
    childPages,
    dispatch,
    LabelSession,
    user,
    objetArray,
    setObjetArray,
    setLabelsCount,
    labelsCount,
    saveUserLocal,
    user_id,
    searchParams,
    setPageName,
    setlabelReference,
    saveSchemaObjectLocal,
    sessionArray,
    labelSessionArray,
    val2,
    VIPSDataServiceURL,
    requestOptions,
    setPageId,
    navigate,
    setIsLoading
  ) {
    // Process schema labels
    processSchemaLabels(val, filteredArray, _values, labelArray2, pageSequence, childPages, dispatch, LabelSession);
    
    // Save user data
    saveUserDataAndState(
      _values, user, {}, objetArray, setObjetArray, 
      setLabelsCount, labelsCount, dispatch, saveUserLocal, 
      user_id, searchParams
    );

    // Extract label information
    const selectedLabel2 = filteredArray[lblCount].split("[");
    const pageName2 = selectedLabel2[1].replace("]", "");
    const labelName = selectedLabel2[0].replace("]", "");
    
    setPageName(pageName2);
    setlabelReference(labelName);
    
    // Save schema object and make API call
    return saveSchemaObjectLocal(
      sessionArray,
      labelSessionArray,
      searchParams,
      val2
    ).then(async (_response) => {
      try {
        const apiResponse = await fetch(
          `${VIPSDataServiceURL}/Schema/GetSchemaPageIdFromLabelType?labelType=${pageName2}&schemaID=${searchParams.get("schema_id")}`,
          requestOptions
        );
        const data = await apiResponse.json();
        localStorage.setItem(
          user_id + "firstPageID",
          JSON.stringify({ firstPageID: data })
        );
        setPageId(data);
        setPageName(pageName2);

        // Store label→PageID mapping so checkout/Dialog can find each label's data
        try {
          var existingMap2 = JSON.parse(localStorage.getItem("labelPageIdMap")) || {};
          existingMap2[labelName] = String(data);
          localStorage.setItem("labelPageIdMap", JSON.stringify(existingMap2));
        } catch (_e) { /* ignore storage errors */ }
        
        const searchString = `&session_id=${searchParams.get("session_id")}&PageID=${data}&schema_id=${searchParams.get("schema_id")}&store_grp=${searchParams.get("store_grp")}&store_grp_desc=${searchParams.get("store_grp_desc")}&label=${labelName}&vd_mode=${searchParams.get("vd_mode")}${searchParams.get("SelectedDivision") ? `&SelectedDivision=${searchParams.get("SelectedDivision")}` : ""}${searchParams.get("Amend") !== null ? `&Amend=${searchParams.get("Amend")}` : ""}${searchParams.get("OrderID") !== null ? `&OrderID=${searchParams.get("OrderID")}` : ""}`;
        
        navigate({
          pathname: "/" + process.env.REACT_APP_ORDERING_PATH,
          search: searchString,
        });

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
      }
    });
  }

  // Helper function to validate labels
  function validateLabels(val, _values, hasLabel, csvUplaodEnabled, getMessage) {
    let atLeastOneLabelSelected = 0;
    
    function checkifAtleastOneLabelSelected(schema) {
      if (_values[schema.fieldName]?.length > 0) {
        atLeastOneLabelSelected = atLeastOneLabelSelected + 1;
      }
    }

    for (const schema of val) {
      if (schema.prefix === "LAB") {
        hasLabel = true;
        checkifAtleastOneLabelSelected(schema);
      }
    }

    if (atLeastOneLabelSelected === 0 && hasLabel && !csvUplaodEnabled) {
      alert(getMessage("atLeastOneLabel"));
      return false;
    }
    
    return true;
  }

  // Helper function to navigate to checkout
  function navigateToCheckout(
    _values,
    val,
    TableType,
    _removeSeperatorValue,
    csvUplaodEnabled,
    saveUserLocal,
    SchemaObject,
    dispatch,
    user,
    userFields,
    objetArray,
    setObjetArray,
    sessionArray,
    setIsLoading,
    navigate,
    createSearchParams,
    location,
    saveSchemaObjectLocal,
    labelSessionArray,
    searchParams,
    val2
  ) {
    // Process static values
    processStaticValues(val, _values, TableType, csvUplaodEnabled, ediReadonlyMode);
    saveUserLocal({ ..._values });

    dispatch(SchemaObject.actions.saveSchemaObject(val));

    // Process new values with DOM attributes
    const newValues = {};
    for (const property in _values) {
      const element = document.querySelector('[fieldname="' + property + '"]');
      let newName = property;
      
      if (element) {
        newName =
          newName +
          "####@###" +
          element.getAttribute("tagname") +
          "####@###" +
          element.getAttribute("prefix");
      }

      newValues[newName] = _values[property];
    }

    const user1 = { ...user, ..._values };
    const user1Fields = { ...userFields, ...newValues };
    
    const promise = saveSchemaObjectLocal(
      sessionArray,
      labelSessionArray,
      searchParams,
      val2
    );
    
    promise.then((_response) => {
      if (Object.entries(user1).length === 0) {
        return;
      } else {
        setObjetArray((current) => [...current, _values]);
        const arr = [...objetArray, _values];
        dispatch(userSlice.actions.saveUser(arr));
        dispatch(userFieldsSlice.actions.saveUserFields(user1Fields));
        dispatch(SessionArray.actions.saveSessionArray(sessionArray));
        setIsLoading(false);
        navigate({
          pathname: "/" + process.env.REACT_APP_ORDERING_PATH + "/CheckOut",
          search: createSearchParams(location.search).toString(),
        });
      }
    });
  }

  var multiLable = false;

  if (pageSequence === 1 && childPages) {
    multiLable = true;
    if (filteredArray.length > 0) {
      if (duplicateElements.length < 1) {
        if (childPages && lblCount < filteredArray.length) {
          if (parseInt(filteredArray.length) > 0) {
            // Process schema labels and create label objects
            processSchemaLabels(val, filteredArray, _values, labelArray2, pageSequence, childPages, dispatch, LabelSession);

            // Save user data and update state
            saveUserDataAndState(
              _values, user, userFields, objetArray, setObjetArray, 
              setLabelsCount, labelsCount, dispatch, saveUserLocal, 
              user_id, searchParams
            );
            
            if (pageSequence === 1) {
              setObjetArray([]);
            }

            // Navigate to next page
            await navigateToNextPage(
              filteredArray, lblCount, setPageName, saveSchemaObjectLocal,
              sessionArray, labelSessionArray, searchParams, val2, GetPageID
            );
          }
        } else {
          sessionCache.set(
            user_id + getSessionObjectId(searchParams, labelSessionArray) + "userOEForm",
            { ..._values }
          );

            // Validate label; on failure show snackbar and stop here
            validateLabelBeforeCheckout().then((isValid) => {
              if (!isValid) {
                return;
              }
              // Navigate to checkout with processed data
              navigateToCheckout(
            _values, val, TableType, removeSeperatorValue, csvUplaodEnabled,
            saveUserLocal, SchemaObject, dispatch, user, userFields, objetArray,
            setObjetArray, sessionArray, setIsLoading, navigate,
            createSearchParams, location, saveSchemaObjectLocal, labelSessionArray, 
            searchParams, val2
              );
            });
        }
      } else {
        alert(getMessage("uniqueLabelReference"));
      }
    } else {
      alert(getMessage("atLeastOneLabel"));
    }
  } else {
    if (childPages && lblCount < filteredArray.length) {
      if (parseInt(filteredArray.length) > 0) {
        // Handle API navigation for child pages
        await handleApiNavigation(
          filteredArray, lblCount, _values, val, labelArray2, pageSequence,
          childPages, dispatch, LabelSession, user, objetArray, setObjetArray,
          setLabelsCount, labelsCount, saveUserLocal, user_id,
          searchParams, setPageName, setlabelReference, saveSchemaObjectLocal,
          sessionArray, labelSessionArray, val2, VIPSDataServiceURL,
          requestOptions, setPageId, navigate, setIsLoading
        );
      }
    } else {
      sessionCache.set(
        user_id + getSessionObjectId(searchParams, labelSessionArray) + "userOEForm",
        { ..._values }
      );
      
      // Process schemas and validate labels
      processStaticValues(val, _values, TableType, csvUplaodEnabled, ediReadonlyMode);
      
      if (!validateLabels(val, _values, hasLabel, csvUplaodEnabled, getMessage)) {
        return;
      }

      saveUserLocal({ ..._values });

      // Validate label; on failure show snackbar and stop here
      if (!(await validateLabelBeforeCheckout())) {
        return;
      }
      // Navigate to checkout with processed data
      navigateToCheckout(
        _values, val, TableType, removeSeperatorValue, csvUplaodEnabled,
        saveUserLocal, SchemaObject, dispatch, user, userFields, objetArray,
        setObjetArray, sessionArray, setIsLoading, navigate, 
        createSearchParams, location, saveSchemaObjectLocal, labelSessionArray, 
        searchParams, val2
      );
    }
  }

  async function GetPageID(pageName2, labelName) {
    setlabelReference(labelName);
    fetch(
      `${VIPSDataServiceURL}` +
        `/Schema/GetSchemaPageIdFromLabelType?labelType=` +
        pageName2 +
        `&schemaID=` +
        searchParams.get("schema_id"),
      requestOptions
    )
      .then((response) => response.json())
      .then((data) => {
        setPageId(data);

        // Store label→PageID mapping so checkout can find each label's data
        try {
          var existingMap = JSON.parse(localStorage.getItem("labelPageIdMap")) || {};
          existingMap[labelName] = String(data);
          localStorage.setItem("labelPageIdMap", JSON.stringify(existingMap));
        } catch (_e) { /* ignore storage errors */ }

        navigate({
          pathname: "/" + process.env.REACT_APP_ORDERING_PATH,
          search:
            `&session_id=` +
            searchParams.get("session_id") +
            `&PageID=` +
            data +
            `&schema_id=` +
            searchParams.get("schema_id") +
            `&store_grp=` +
            searchParams.get("store_grp") +
            `&store_grp_desc=` +
            searchParams.get("store_grp_desc") +
            `&label=` +
            labelName +
            `&labelType=` + encodeURIComponent(pageName2) +
            `&vd_mode=` +
            searchParams.get("vd_mode") +
            (searchParams.get("SelectedDivision")
              ? `&SelectedDivision=` + searchParams.get("SelectedDivision")
              : "") +
            (searchParams.get("Amend") !== null
              ? `&Amend=` + searchParams.get("Amend")
              : "") +
            (searchParams.get("OrderID") !== null
              ? `&OrderID=` + searchParams.get("OrderID")
              : ""),
        });

        setIsLoading(false);
      })

      .catch((_err) => {
        setIsLoading(false);
      });
  }
};

export const onSubmitOCC = async ({
  values,
  sessionArray,
  setLabels,
  setLabelsCount,
  setObjetArray,
  setLabelArray,
  dispatch,
  LabelSession,
  labelArray2,
  objetArray,
  user,
  userFields,
  saveUserLocal,
  searchParams,
  labels,
  labelsCount2,
  labelSessionArray,
  val,
  val2,
  setLabelsCount2,
  setPageId,
  setIsLoading,
  navigate,
  TableType,
  removeSeperatorValue,
  saveSchemaObjectLocal,
  SchemaObject,
  getMessage,
  setSnackbarMessage,
  setOpenSnackbar,
  childPages,
  pageSequence,
  setPageName,
  setlabelReference,
  VIPSDataServiceURL,
  requestOptions,
  location,
  createSearchParams,
}) => {
    let user_id = "";
  sessionArray.forEach(function (item) {
    if (item.Key == "user_id") {
      user_id = item.Value;
    }
  });

  let labelArray;
  let lblCount;
  let lblCount2;
  let newArray = labelSessionArray.map((element) => {
    let parts = element.split("[");
    return parts[0];
  });
  var index = newArray.indexOf(searchParams.get("label"));
  lblCount2 = index + 1;
  if (pageSequence === 1 && childPages) {
    let labelSelectionPageFields = Object.values(values);
    setLabels(labelSelectionPageFields);
    labelArray = labelSelectionPageFields;
    setLabelsCount(0);
    lblCount = 0;
  }
  lblCount2 = lblCount2 ?? labelsCount2;
  labelArray = labelArray ?? labels;
  let labelArray3 = [];
  let testArray1 = [];
  if (lblCount2 < labelSessionArray.length) {
    let pageFields = Object.values(values);
    let pageName;
  if (parseInt(labelSessionArray.length) > 0) {
    val.forEach((schema, index) => {
        if (labelSessionArray[index] != undefined) {
          if (pageFields[index]) {
            const selectedLabelEntry = labelSessionArray[index];
            if (typeof selectedLabelEntry !== "string") return;
            var selectedLabel = selectedLabelEntry.split("[");
            pageName = selectedLabel[1].replace("]", "");
            var labelRef = selectedLabel[0].replace("]", "");

            const labelObj = {
              fieldName: schema.fieldName,
              tagName: schema.tagName,
              prefix: schema.prefix,
              labelRef,
            };
            if (pageSequence === 1 && childPages) {
              labelArray3 = [...labelArray3, labelObj];
              setLabelArray(labelArray3);
              dispatch(
                LabelSession.actions.saveLabelSession({
                  LabelSession: labelArray3,
                })
              );
            }
          }
        }
      });
      const arr = [values];
      setObjetArray((current) => [...current, values]);

      testArray1 = testArray1 ?? objetArray;

      const user1 = { ...user, ...values };
      saveUserLocal({ ...values });
      dispatch(userSlice.actions.saveUser(testArray1));
      var objectID =
        user_id +
        getSessionObjectId(searchParams, labelSessionArray) +
        "userOEForm";
      sessionCache.set(objectID, { ...values });

      var selectedLabel = labelSessionArray[lblCount2].split("[");
      var labelType = selectedLabel[1].replace("]", "");
      var labelRef = selectedLabel[0].replace("]", "");
      return saveSchemaObjectLocal(
        sessionArray,
        labelSessionArray,
        searchParams,
        val2
      ).then(async (_response) => {
        await fetch(
          `${VIPSDataServiceURL}` +
            `/Schema/GetSchemaPageIdFromLabelType?labelType=` +
            labelType +
            `&schemaID=` +
            searchParams.get("schema_id"),
          requestOptions
        )
          .then((response) => response.json())
          .then((data) => {
            setPageId(data);
            navigate({
              pathname: "/" + process.env.REACT_APP_ORDERING_PATH,
              search:
                `&session_id=` +
                searchParams.get("session_id") +
                `&PageID=` +
                data +
                `&schema_id=` +
                searchParams.get("schema_id") +
                `&store_grp=` +
                searchParams.get("store_grp") +
                `&store_grp_desc=` +
                searchParams.get("store_grp_desc") +
                `&vd_mode=` +
                searchParams.get("vd_mode") +
                `&label=` +
                labelRef +
                `&OCC=True` +
                `&vipsOrder=Inprogress` +
                (searchParams.get("SelectedDivision")
                  ? `&SelectedDivision=` + searchParams.get("SelectedDivision")
                  : "") +
                (searchParams.get("OrderID") !== null
                  ? `&OrderID=` + searchParams.get("OrderID")
                  : ""),
            });

            setIsLoading(false);
          })
          .catch((_err) => {
            setIsLoading(false);
          });
      });
    }
  } else {
    var objectID =
      user_id +
      getSessionObjectId(searchParams, labelSessionArray) +
      "userOEForm";
    sessionCache.set(objectID, { ...values });

    for (const schema of val) {
      if (schema.dataType === TableType) {
        for (const data of schema.schemaPageSubItems) {
          removeSeperatorValue(data, schema.fieldName, values);
          addStaticValue(data, schema.fieldName);
        }
      } else {
        removeSeperatorValue(schema);
      }
    }
    saveUserLocal({ ...values });
    function addStaticValue(schema, tableName) {
      if (schema.prefix === "STC") {
        for (const data of values[tableName]) {
          if (Object.values(data).some((x) => x?.length > 0)) {
            data[schema.fieldName] = schema.value;
          }
        }
      }
    }

    dispatch(SchemaObject.actions.saveSchemaObject(val2));

    const newValues = {};
    for (const property in values) {
      var element = document.querySelector('[fieldname="' + property + '"]');

      var newName = property;
      if (element) {
        newName =
          newName +
          "####@###" +
          element.getAttribute("tagname") +
          "####@###" +
          element.getAttribute("prefix");
      }

      newValues[newName] = values[property];
    }

    const user1 = { ...user, ...values };
    const user1Fields = {
      ...userFields,
      ...newValues,
    };
    return saveSchemaObjectLocal(
      sessionArray,
      labelSessionArray,
      searchParams,
      val2
    ).then((_response) => {
      if (Object.entries(user1).length === 0) {
        return;
      } else {
        setObjetArray((current) => [...current, values]);
        const arr = [...objetArray, values];
        dispatch(userSlice.actions.saveUser(arr));
        dispatch(userFieldsSlice.actions.saveUserFields(user1Fields));
        dispatch(SessionArray.actions.saveSessionArray(sessionArray));
        setIsLoading(false);
        navigate({
          pathname: "/" + process.env.REACT_APP_ORDERING_PATH + "/CheckOut",
          search: createSearchParams(location.search).toString(),
        });
      }
    });
  }
};
